import { useCallback } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

interface Props {
  contributionDate?: string;
  plansIds?: string[];
  organizationIds?: string[];
  employeeIds?: string[];
  enabled?: boolean;
  payrolls?: string[];
}

const getTotalEmployeeContributionView = async ({
  contributionDate,
  plansIds,
  payrolls,
  organizationIds,
  employeeIds,
}: Props) => {
  return api.get<number>(API_URL.MANUAL_CONTRIBUTION_EMPLOYEES_COUNT, {
    params: {
      ...(contributionDate
        ? {
            contribution_date: dayjs(contributionDate).format(RAW_DATE_FORMAT),
          }
        : {}),
      ...(payrolls && payrolls.length ? { payroll_group_codes: payrolls.join(',') } : {}),
      ...(plansIds && plansIds.length ? { plan_ids: plansIds.join(',') } : {}),
      ...(organizationIds && organizationIds.length ? { organization_ids: organizationIds.join(',') } : {}),
      ...(employeeIds && employeeIds.length ? { employee_ids: employeeIds.join(',') } : {}),
    },
  });
};

export const QUERY_KEY = 'useGetTotalEmployeesContributionViewQueryKey';

export const useGetTotalEmployeesContributionViewQuery = ({
  contributionDate,
  payrolls,
  plansIds,
  organizationIds,
  employeeIds,
  enabled = false,
}: Props) => {
  const { data, isLoading, isSuccess } = useQuery(
    [QUERY_KEY],
    () =>
      getTotalEmployeeContributionView({
        contributionDate,
        plansIds,
        payrolls,
        organizationIds,
        employeeIds,
      }),
    {
      enabled,
    }
  );

  const fetchTotalManually = useCallback(async () => {
    return await getTotalEmployeeContributionView({
      contributionDate,
      plansIds,
      payrolls,
      organizationIds,
      employeeIds,
    });
  }, [contributionDate, employeeIds, organizationIds, plansIds, payrolls]);

  return {
    isLoading,
    data: data?.data,
    isSuccess,
    fetchTotalManually,
  };
};
